import { Button } from "@capacit-com/lara-law-shared-ui";
import AppRoutes from "@/constants/routes/AppRoutes";
import useConversation from "@/hooks/useConversation";
import usePiiOverride from "@/hooks/usePiiOverride";
import usePiiFeatureEnabled from "@/hooks/usePiiFeatureEnabled";
import Message from "@/models/conversation/Message";
import { ArrowLeft, ShieldAlert } from "lucide-react";
import ConversationDeleteAction from "../insights/components/ConversationDeleteAction";
import MessageBubble from "@/pages/chat/components/MessageBubble";
import { FC, useEffect, useState, useRef, useMemo } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Card, CardTitle, CardHeader } from "@capacit-com/lara-law-shared-ui";
import {
  createHourlyRatingGraph,
  getMessageRatings,
  filterMessagesByDateRange,
} from "./helpers/UserConvOverviewHelper";
import LineChartComponent from "../insights/components/charts/LineChartComponent";
import UserConversationStatistics from "./components/UserConversationStatistics";
import Filters from "../insights/components/Filters";
import { DateTime } from "luxon";
import type { DateRange } from "@capacit-com/lara-law-shared-ui";
import UserConversationSkeleton from "./components/UserConversationSkeleton";
import ChainOfThoughtBubble from "@/pages/chat/components/ChainOfThoughtBubble";
import { Checkbox } from "@capacit-com/lara-law-shared-ui";
import useLawTermToggleStore from "@/stores/lawtermtoggle.store";
import Conversation from "@/models/conversation/Conversation";
import SelectedFilters from "@/pages/chat/components/filter/SelectedFilters";

const UserConversationOverview: FC = () => {
  const { conversationId, isDeleted } = useParams();
  const location = useLocation();
  const title = (location.state as { conversationTitle?: string })
    ?.conversationTitle;
  const navigate = useNavigate();
  const { useQueryMessagesByConversationId, useQuerySingleConversation } =
    useConversation();

  const { toggleLawTermShow, setToggleLawTermShow } = useLawTermToggleStore();
  const [messages, setMessages] = useState<Message[]>([]);
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [hasScrollbar, setHasScrollbar] = useState(false);
  const [presetText, setPresetText] = useState<string | undefined>();
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const isConversationDeleted = isDeleted === "true";

  const defaultDateRange = {
    from: DateTime.now().minus({ days: 7 }).startOf("day").toJSDate(),
    to: DateTime.now().endOf("day").toJSDate(),
  };

  const [dateRange, setDateRange] = useState<DateRange | undefined>({
    from: defaultDateRange.from,
    to: defaultDateRange.to,
  });

  const fetchedMessages = useQueryMessagesByConversationId(conversationId);
  const fetchedConversation = isConversationDeleted
    ? undefined
    : // eslint-disable-next-line react-hooks/rules-of-hooks
      useQuerySingleConversation(conversationId);

  // PII-override audit for this conversation (LAR-27 #5). The audit trail is
  // always recorded server-side while the feature runs; the fetch + banner are
  // hidden while the PII-Redaction flag is off (feature invisible and inert).
  const piiFeatureEnabled = usePiiFeatureEnabled();
  const { useQueryByConversation } = usePiiOverride();
  const overrideAudits = useQueryByConversation(
    conversationId || "",
    piiFeatureEnabled
  );
  const overrideCount = (overrideAudits.data ?? []).reduce(
    (sum, audit) => sum + audit.overriddenCount,
    0
  );
  const hasHardOverride = (overrideAudits.data ?? []).some(
    (audit) => audit.hardIdOverridden
  );

  useEffect(() => {
    if (!conversationId) {
      navigate(AppRoutes.insightsUserOverviewNotFound, {
        replace: true,
      });
      return;
    }
  }, [conversationId, navigate]);

  useEffect(() => {
    if (!isConversationDeleted) {
      if (fetchedMessages.isFetched && fetchedMessages.data) {
        setMessages(fetchedMessages.data);
      }
      if (
        fetchedConversation &&
        fetchedConversation.isFetched &&
        fetchedConversation.data
      ) {
        setConversation(fetchedConversation.data);
      }
      if (
        fetchedMessages.error ||
        (fetchedConversation && fetchedConversation.error)
      ) {
        navigate(AppRoutes.insightsUserOverviewNotFound, {
          replace: true,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchedMessages, fetchedConversation]);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (container) {
      setHasScrollbar(container.scrollHeight > container.clientHeight);
    }
  }, [messages]);

  useEffect(() => {
    setPresetText("Sidste 7 dage");
  }, []);

  const ratingStats = useMemo(() => {
    const range =
      dateRange?.from && dateRange?.to
        ? {
            from: DateTime.fromJSDate(dateRange.from).startOf("day").toJSDate(),
            to: DateTime.fromJSDate(dateRange.to).endOf("day").toJSDate(),
          }
        : undefined;
    const filteredMessages = filterMessagesByDateRange(messages, range);
    const stats = createHourlyRatingGraph(filteredMessages, range);
    const ratings = getMessageRatings(filteredMessages);
    return {
      graphData: stats,
      messageCount: filteredMessages.length,
      averageRating: ratings.average,
    };
  }, [messages, dateRange]);

  return (
    <div className="flex flex-col p-4 xl:px-8 gap-4">
      <div className="flex justify-between">
        <Button
          variant="outline"
          size="sm"
          className="border-primary flex text-xs"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft /> Tilbage til insights
        </Button>
        {!isConversationDeleted && conversationId && (
          <ConversationDeleteAction
            conversationId={conversationId}
            conversationTitle={title}
            appearance="labelled"
            onDeleted={() => navigate(AppRoutes.insights)}
          />
        )}
      </div>
      {/* Also gated on the flag: disabling the query does not clear React
          Query's cache, so stale audits must not resurrect the banner after
          the kill-switch turns the feature off. */}
      {piiFeatureEnabled && overrideCount > 0 && (
        <div className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning/10 p-3">
          <ShieldAlert className="mt-0.5 size-4 shrink-0 text-warning" />
          <div className="text-sm text-foreground">
            <p className="font-medium">
              Persondata tilsidesat i denne samtale
            </p>
            <p className="text-muted-foreground">
              {overrideCount}{" "}
              {overrideCount === 1 ? "oplysning" : "oplysninger"} blev sendt
              uden anonymisering
              {hasHardOverride
                ? " — heraf CPR-numre, telefonnumre, e-mailadresser eller kontonumre"
                : ""}
              .
            </p>
          </div>
        </div>
      )}
      {fetchedMessages.isPending ? (
        <UserConversationSkeleton />
      ) : (
        <>
          <UserConversationStatistics
            messages={filterMessagesByDateRange(
              messages,
              dateRange?.from && dateRange?.to
                ? {
                    from: DateTime.fromJSDate(dateRange.from)
                      .setZone("utc")
                      .toJSDate(),
                    to: DateTime.fromJSDate(dateRange.to)
                      .setZone("utc")
                      .toJSDate(),
                  }
                : undefined
            )}
            conversationTitle={title || ""}
            filterComponent={
              <Filters
                presetText={presetText}
                setPresetText={setPresetText}
                date={dateRange}
                setDate={setDateRange}
                titlePrefix="Statistikker for: "
              />
            }
          />

          <Card className="p-4 rounded-lg shadow-md border-none">
            <CardHeader className="p-0 pb-2 border-b">
              <div className="flex justify-between">
                <CardTitle className="flex flex-row items-center">
                  Chatlog{" "}
                  {fetchedConversation?.isFetched && conversation ? (
                    <div className="ml-2 border-destructive/30">
                      <SelectedFilters
                        selectedConversation={conversation}
                        isOverview={true}
                      />
                    </div>
                  ) : fetchedConversation?.isPending ? (
                    <div>Indlæser...</div>
                  ) : (
                    <div>Ingen samtale filtre fundet</div>
                  )}
                </CardTitle>
                <div className="flex items-center space-x-2">
                  <p className="text-sm text-foreground">
                    Vis juridiske termer i chatten
                  </p>
                  <Checkbox
                    checked={toggleLawTermShow}
                    onCheckedChange={() =>
                      setToggleLawTermShow(!toggleLawTermShow)
                    }
                  />
                </div>
              </div>
            </CardHeader>
            {isConversationDeleted ? (
              <div className="flex flex-col gap-4 pl-10 py-6">
                <p className="text-sm text-foreground text-center">
                  Denne chat er blevet slettet.
                </p>
              </div>
            ) : (
              <div
                ref={messagesContainerRef}
                className={`flex flex-col gap-4 pl-10 py-6 ${
                  hasScrollbar ? "pr-12" : "pr-10"
                } max-h-[500px] overflow-y-auto`}
              >
                {messages
                  .sort(
                    (a, b) =>
                      new Date(a.createdAt).getTime() -
                      new Date(b.createdAt).getTime()
                  )
                  .map((message) => (
                    <div key={message.id}>
                      <ChainOfThoughtBubble
                        message={message}
                        isOverview={true}
                      />
                      <MessageBubble
                        key={message.id}
                        message={message}
                        isOverview={true}
                      />
                    </div>
                  ))}
              </div>
            )}
          </Card>
          <LineChartComponent
            graphStats={ratingStats.graphData.map((stat) => ({
              ...stat,
              wordConversationCount: undefined,
            }))}
            type="messages"
            title="Message Ratings Over Time"
          />
        </>
      )}
    </div>
  );
};

export default UserConversationOverview;
